import type { CallScript } from "@frontierx/shared/schemas/CallScript";
import { CallScriptSchema } from "@frontierx/shared/schemas/CallScript";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useAuth, useOrganization } from "../Auth/UnifiedAuthProvider";

export const CallScriptsResponseSchema = z.object({
  callScripts: z.array(CallScriptSchema),
});

export type CallScriptsResponse = z.infer<typeof CallScriptsResponseSchema>;

export interface CallScriptSelectorProps {
  selectedScript: CallScript | null;
  onSelect: (c: CallScript) => void;
  label?: string;
  size?: "sm" | "default" | "lg";
}

declare const __API_URL__: string;

export const CallScriptSelector = ({
  selectedScript,
  onSelect,
  label = "Select Script",
  size = "default",
}: Readonly<CallScriptSelectorProps>) => {
  const { organization } = useOrganization();
  const { jwt } = useAuth();

  const fetchCallScripts = async (
    orgId: string,
  ): Promise<CallScriptsResponse> => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (jwt) {
      headers.Authorization = `Bearer ${jwt}`;
    }

    const response = await fetch(
      `${__API_URL__}/api/call-scripts?orgId=${encodeURIComponent(orgId)}`,
      {
        method: "GET",
        headers,
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return CallScriptsResponseSchema.parse(result);
  };

  const {
    data: callScriptsResponse,
    isLoading: isLoadingScripts,
    isError: isErrorScripts,
  } = useQuery<CallScriptsResponse, Error>({
    queryKey: ["callScripts", organization?.id],
    queryFn: () => fetchCallScripts(organization?.id ?? ""),
    enabled: !!organization?.id,
  });

  const callScripts = callScriptsResponse?.callScripts ?? [];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" disabled={isLoadingScripts} size={size}>
          {selectedScript ? selectedScript.title : label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {isLoadingScripts ? (
          <DropdownMenuItem disabled>Loading scripts...</DropdownMenuItem>
        ) : isErrorScripts ? (
          <DropdownMenuItem disabled>Error loading scripts</DropdownMenuItem>
        ) : callScripts && callScripts.length > 0 ? (
          callScripts.map((script) => (
            <DropdownMenuItem key={script.id} onClick={() => onSelect(script)}>
              {script.title}
            </DropdownMenuItem>
          ))
        ) : (
          <DropdownMenuItem disabled>
            Manage call scripts in the web dashboard
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
